'use client';

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Cake, Plus, Search, ShoppingCart, Sparkles, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCurrentBusiness } from '@/lib/business-context';
import { useAddPostModal } from '@/components/marketplace/add-post-modal';
import { NotificationCenter } from '@/components/crm/notification-center';
import { WebOnlyBillingAction } from '@/components/billing/web-only-billing-action';
import { cn } from '@/lib/utils';
import { isSettingsRoute } from '@/lib/settings-route';
import { useFeatureFlag } from '@/lib/feature-flags';

const SEARCH_DEBOUNCE_MS = 300;

type BusinessAvatarData = {
  id: string;
  name: string;
  avatarUrl?: string | null;
};

function normalizeBusinessName(name: unknown): string {
  return typeof name === 'string' ? name.trim() : typeof name === 'number' ? String(name) : 'Store';
}

function getInitials(name: string | unknown): string {
  const normalized = normalizeBusinessName(name).trim();
  const words = normalized.split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'SS';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

function BusinessAvatarStack({
  businesses,
  maxVisible = 2,
}: {
  businesses: BusinessAvatarData[];
  maxVisible?: number;
}) {
  const visibleBusinesses = businesses.slice(0, maxVisible);

  if (visibleBusinesses.length === 0) {
    return (
      <div className="h-6 w-6 overflow-hidden rounded-full ring-2 ring-background">
        <div className="flex h-full w-full items-center justify-center bg-muted text-[10px] font-semibold text-muted-foreground">
          SS
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center">
      {visibleBusinesses.map((business, index) => (
        <div
          key={business.id}
          className={cn(
            'h-6 w-6 overflow-hidden rounded-full ring-2 ring-background',
            index > 0 && '-ml-2',
          )}
        >
          {business.avatarUrl ? (
            <img src={business.avatarUrl} alt={business.name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted text-[10px] font-semibold text-muted-foreground">
              {getInitials(business.name)}
            </div>
          )}
        </div>
      ))}
      {businesses.length > visibleBusinesses.length && (
        <div className="-ml-2 flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground ring-2 ring-background">
          +{businesses.length - visibleBusinesses.length}
        </div>
      )}
    </div>
  );
}

export function AppTopBar({
  cartCount,
  onCartClick,
  showNotifications = false,
  onSpotClick,
  spotOpen = false,
  className,
}: {
  cartCount: number;
  onCartClick: () => void;
  /**
   * Spot (the assistant) entry point. The top bar is the one surface with
   * room at every breakpoint — the bottom tab bar is full and a floating
   * button fought the tab-bar island — so the app shell passes this and
   * the public storefront leaves it out (signed-in only anyway).
   */
  onSpotClick?: () => void;
  spotOpen?: boolean;
  /** Positioning is the shell's call: the app shell fixes it on phones, the storefront keeps it in flow. */
  className?: string;
  /**
   * CRM alerts are a seller-side control (with Dismiss and Mark-all-read), and
   * this bar is shared with the public storefront shell. Off by default so a
   * customer-facing page never mounts it — or its 60s alerts poll.
   */
  showNotifications?: boolean;
}) {
  const pathname = usePathname();
  const feedEnabled = useFeatureFlag('nav.feed');
  const showPricing = isSettingsRoute(pathname);
  const router = useRouter();
  const { businesses, currentBusiness, currentBusinessId, loading: bizLoading } = useCurrentBusiness();
  const { openAddPostModal } = useAddPostModal();
  const searchParams = useSearchParams();
  const paramsString = searchParams.toString();
  const marketplaceQuery = (searchParams.get('q') ?? '').trim();
  const isMessagesRoute = pathname === '/messages' || pathname.startsWith('/messages/');
  const [searchText, setSearchText] = useState(marketplaceQuery);
  const isLoggedIn = Boolean(currentBusinessId && (businesses.length > 0 || bizLoading));

  const avatarBusinesses = useMemo<BusinessAvatarData[]>(() => {
    if (!Array.isArray(businesses) || businesses.length === 0) return [];
    const normalizedBusinesses = businesses
      .filter((business) => Boolean(business && typeof business.id === 'string' && business.id.trim().length > 0))
      .map((business) => ({
        id: business.id,
        name: normalizeBusinessName(business.name),
        avatarUrl: business.avatarUrl ?? null,
      }));
    if (normalizedBusinesses.length === 0) return [];

    const selectedBusiness = normalizedBusinesses.find((business) => business.id === currentBusiness?.id);
    const ordered = selectedBusiness
      ? [selectedBusiness, ...normalizedBusinesses.filter((business) => business.id !== selectedBusiness.id)]
      : normalizedBusinesses;

    const firstBusiness = ordered[0];
    const preferredBusiness = firstBusiness?.avatarUrl
      ? firstBusiness
      : ordered.find((business) => Boolean(business?.avatarUrl)) ?? firstBusiness;

    if (!preferredBusiness) return [];

    const rest = ordered.filter((business) => business.id !== preferredBusiness.id);
    const combined = [preferredBusiness, ...rest].filter((business, index, list) => {
      return business && index === list.findIndex((candidate) => candidate.id === business.id);
    });

    return combined.map((business) => ({
      id: business.id,
      name: business.name,
      avatarUrl: business.avatarUrl ?? null,
    }));
  }, [businesses, currentBusiness?.id]);

  useEffect(() => {
    if (pathname !== '/marketplace') return;
    setSearchText(marketplaceQuery);
  }, [pathname, marketplaceQuery]);

  useEffect(() => {
    if (pathname !== '/marketplace') return;
    const nextQuery = searchText.trim();
    if (nextQuery === marketplaceQuery) return;

    const timer = setTimeout(() => {
      const params = new URLSearchParams(paramsString);
      if (nextQuery) params.set('q', nextQuery);
      else params.delete('q');
      const url = params.toString() ? `/marketplace?${params.toString()}` : '/marketplace';
      router.replace(url, { scroll: false });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [pathname, searchText, marketplaceQuery, paramsString, router]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = searchText.trim();

    if (pathname === '/marketplace') {
      const params = new URLSearchParams(paramsString);
      if (query) params.set('q', query);
      else params.delete('q');
      const url = params.toString() ? `/marketplace?${params.toString()}` : '/marketplace';
      router.replace(url, { scroll: false });
      return;
    }

    const url = query ? `/marketplace?q=${encodeURIComponent(query)}` : '/marketplace';
    router.push(url);
  };

  const handleAddPost = () => {
    openAddPostModal();
  };

  return (
    <header className={cn('flex-none z-50 ios-bar ios-bar-top pt-[env(safe-area-inset-top)]', className)}>
      <div className="flex h-16 items-center gap-2 px-3 sm:gap-3 sm:px-4 md:h-14 md:px-6">
        <Link href="/marketplace" aria-label="Sweetspot home" className="flex items-center gap-2 shrink-0">
          <Cake className="h-6 w-6 text-primary md:h-6 md:w-6" />
          <span className="hidden text-base font-semibold sm:inline">Sweetspot</span>
        </Link>

        {!isLoggedIn && feedEnabled && <Link href="/feed" prefetch={false} aria-current={pathname === '/feed' ? 'page' : undefined}
          className="hidden min-h-11 items-center rounded-md px-2 text-sm font-medium hover:bg-muted md:inline-flex">Feed</Link>}
        {!showPricing && (
          <Link
            href="/about"
            className="hidden min-h-9 items-center rounded-md px-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground lg:inline-flex"
          >
            About
          </Link>
        )}
        {showPricing && <WebOnlyBillingAction>
          <Link
            href="/pricing"
            className="hidden min-h-9 items-center rounded-md px-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground xl:inline-flex"
          >
            Pricing
          </Link>
        </WebOnlyBillingAction>}

        <form
          onSubmit={handleSubmit}
          className={cn(
            'relative min-w-0 flex-1',
            isMessagesRoute && 'sm:max-w-none',
          )}
        >
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search stores..."
            className="h-10 pl-8 pr-3 text-sm md:h-9"
            aria-label="Search stores"
          />
        </form>

        <div className="ml-auto flex items-center gap-1.5 shrink-0 sm:gap-2">
          {onSpotClick && isLoggedIn && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(
                'h-10 w-10 p-0 sm:w-auto sm:px-3 md:h-9 gap-1.5',
                spotOpen && 'border-primary/40 bg-primary/10 text-primary',
              )}
              onClick={onSpotClick}
              aria-label="Ask Spot"
              aria-pressed={spotOpen}
            >
              <Sparkles className="h-5 w-5 text-primary md:h-4 md:w-4" />
              <span className="hidden sm:inline">Spot</span>
            </Button>
          )}
          {/* Notifications — the single instance, at every breakpoint. Hiding a
              second copy with CSS would still mount it and run its 60s poll. */}
          {showNotifications && isLoggedIn && <NotificationCenter />}
          {/* Marketplace / Messages / Orders are not repeated here: on desktop the
              sidebar already carries all three, and on mobile they are in the
              bottom nav. */}
          {/* Dashboard avatar — hidden on mobile (in bottom nav) */}
          {isLoggedIn ? (
            <Button asChild variant="outline" size="sm" className="hidden md:inline-flex h-9 gap-1.5 px-2" aria-label="Go to dashboard">
              <Link href="/dashboard">
                {/* Decorative: marks the stack as "mine". The button still goes
                    to /dashboard, so it gets no label of its own. */}
                <User className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <BusinessAvatarStack businesses={avatarBusinesses} />
              </Link>
            </Button>
          ) : (
            <Button asChild variant="outline" size="sm" className="hidden md:inline-flex h-9 px-3 gap-1.5" aria-label="Sign in">
              <Link href="/login">Sign In</Link>
            </Button>
          )}
          {/* Add Post / Sign Up — always visible */}
          {isLoggedIn ? (
            <Button type="button" size="sm" className="h-10 w-10 p-0 sm:w-auto sm:px-3 md:h-9 gap-1.5" onClick={handleAddPost}>
              <Plus className="h-5 w-5 md:h-4 md:w-4" />
              <span className="hidden sm:inline">Add Post</span>
            </Button>
          ) : (
            <Button asChild type="button" size="sm" className="h-10 px-3 md:h-9 gap-1.5">
              <Link href="/signup" aria-label="Sign up">
                <Plus className="h-5 w-5 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Sign Up</span>
              </Link>
            </Button>
          )}
          {/* Cart — always visible */}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="relative h-10 w-10 p-0 sm:h-10 sm:w-auto sm:px-2 md:h-9 gap-1.5"
            onClick={onCartClick}
            aria-label="Open cart"
          >
            <ShoppingCart className="h-5 w-5" />
            {cartCount > 0 && (
              <span className="absolute -right-1.5 -top-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-0 text-[11px] font-bold leading-none text-destructive-foreground sm:static sm:ml-1">
                {cartCount > 99 ? '99+' : cartCount}
              </span>
            )}
          </Button>
        </div>
      </div>
    </header>
  );
}
